import { Resources } from 'constants/resources';

import { yupResolver } from '@hookform/resolvers/yup';
import { PresetsEditGeneralDialogSkeleton } from 'pages/Presets/components/PresetsEditGeneralDialogSkeleton';
import { useGatewayCurrencies } from 'pages/Presets/hooks/useGatewayCurrencies';
import { PRESETS_EDIT_GENERAL_SCHEMA } from 'pages/Presets/schemas/schema';
import { queryClient } from 'providers/queryClient';
import { Form, useNotify, useUpdate } from 'react-admin';
import { FieldValues } from 'react-hook-form';
import { Box } from 'shared/mui/Box';
import { CancelButton, SaveButton } from 'shared/mui/Buttons';
import { Card, CardActionsButtons, CardHeader } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { Dialog, DialogProps } from 'shared/mui/Dialogs';
import { TextField } from 'shared/mui/Fields';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { AutocompleteInput } from 'shared/react-admin/Inputs';
import { TextInput } from 'shared/react-admin/Inputs/TextInputs/TextInput/TextInput';
import { ReferenceInput } from 'shared/react-admin/ReferenceInputs';
import { Preset } from 'types/embedded/preset';
import { cleanEmpty } from 'utils/cleanEmpty';

type Props = {
  record: Preset;
  onClose: () => void;
} & Pick<DialogProps, 'open'>;

type FormValues = {
  currency: string;
  paymentComment: string | null;
};

export const PresetsEditGeneralDialog: React.FC<Props> = ({ record, open, onClose }) => {
  const notify = useNotify();
  const [update] = useUpdate();

  const { currencyIds, isLoading } = useGatewayCurrencies(record.gateway?.['@id']);

  const handleSubmit = (data: FormValues) => {
    update(
      Resources.Presets,
      {
        id: record.id,
        data: { currency: data.currency, paymentComment: data.paymentComment || null },
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: [Resources.Presets] });
          notify('Общая информация успешно изменена', { type: 'info' });
          onClose();
        },
        onError: (error) => {
          notify(`Ошибка: ${(error as Error).message || 'ошибка сервера'}`, {
            type: 'error',
            autoHideDuration: 3000,
          });
        },
      },
    );
  };

  return (
    <Dialog onClose={onClose} open={open} width="640px">
      <Form
        onSubmit={(data: FieldValues) => handleSubmit(data as FormValues)}
        record={{
          currency: record.currency?.['@id'],
          paymentComment: record.paymentComment ?? '',
        }}
        resolver={yupResolver<FieldValues>(PRESETS_EDIT_GENERAL_SCHEMA)}
      >
        <Card>
          <CardHeader
            appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
            title="Редактирование общей информации"
            titleSize="smallBold"
          />
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {isLoading ? (
              <PresetsEditGeneralDialogSkeleton />
            ) : (
              <>
                <Box sx={{ display: 'grid', gap: '16px', gridTemplateColumns: '1fr 1fr' }}>
                  <TextField
                    disabled
                    fullWidth
                    label="Мерчант"
                    size="small"
                    value={record.merchant?.name ?? ''}
                    variant="outlined"
                  />
                  <TextField
                    disabled
                    fullWidth
                    label="Шлюз"
                    size="small"
                    value={record.gateway?.name ?? ''}
                    variant="outlined"
                  />
                </Box>
                <ReferenceInput
                  filter={cleanEmpty({
                    id: currencyIds.length ? currencyIds : undefined,
                    active: true,
                  })}
                  perPage={50}
                  reference="currencies"
                  source="currency"
                >
                  <AutocompleteInput
                    filterToQuery={(searchText: string) => ({ currency: searchText })}
                    fullWidth
                    helperText={false}
                    isRequired
                    label="Валюта"
                    optionText={(r) => `${r?.alphaCode}, ${r?.name}`}
                    variant="outlined"
                  />
                </ReferenceInput>
                <TextInput
                  fullWidth
                  helperText={false}
                  label="Общий комментарий пресетов пополнения"
                  multiline
                  source="paymentComment"
                  variant="outlined"
                />
              </>
            )}
          </CardContent>
          <CardActionsButtons
            rightActionsSlot={[
              <CancelButton key="cancel" onClick={onClose} variant="outlined" />,
              <SaveButton key="save" label="Сохранить" />,
            ]}
          />
        </Card>
      </Form>
    </Dialog>
  );
};
